import { api, qs } from "./clientService";
import type {
  DeviceCreateRequest,
  DeviceUpdateRequest,
  DeviceListQuery,
} from "../interface/requests/deviceRequest";
import type {
  DeviceCreateResponse,
  DeviceDetailResponse,
  DeviceHeartbeatResponse,
  DeviceListResponse,
  DeviceUpdateResponse,
} from "../interface/responses/deviceResponse";
import { LicenseValidationResponse } from "@/interface/responses/licenseResponse";

/** POST /api/v1/devices/create */
export function createDevice(payload: DeviceCreateRequest) {
  return api<DeviceCreateResponse>("/api/v1/devices/create", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Enhanced query interface to match API parameters */
export interface DeviceListQueryWithFilters extends DeviceListQuery {
  limit?: number;
  page?: number;
  q?: string; // Search query (for device name, location, email, phone)
  order?: "ASC" | "DESC"; // Only when sortBy is provided
  sortBy?: "device.last_heartbeat" | "device.firmware_version" | "user.phone_number"; // Available sort fields
  device_type?: "Mobile" | "STB";
  deviceGroupIds?: number[]; // Array of device group IDs
  network_interface?: "WIFI" | "ETHERNET" | "SIM";
  network_quality?: "EXCELLENT" | "GOOD" | "FAIR" | "POOR";
}

/** GET /api/v1/devices/list with enhanced filtering */
export function listDevices(query: DeviceListQueryWithFilters = {}) {
  return api<DeviceListResponse>("/api/v1/devices/list" + qs(query), {
    method: "GET",
  });
}

/** GET /api/v1/devices/all with enhanced filtering */
export function listAllDevices(query: DeviceListQueryWithFilters = {}) {
  return api<DeviceListResponse>("/api/v1/devices/all" + qs(query), {
    method: "GET",
  });
}

/** GET /api/v1/devices/{id} */
export function getDeviceById(id: number) {
  return api<DeviceDetailResponse>(`/api/v1/devices/${id}`, { method: "GET" });
}

/** PATCH /api/v1/devices/{id} */
export function updateDevice(id: number, payload: DeviceUpdateRequest) {
  return api<DeviceUpdateResponse>(`/api/v1/devices/update/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** DELETE /api/v1/devices/{id}
 * API docs show 200 OK with no body. Our client returns `null` for empty payloads.
 */
export function deleteDevice(id: number) {
  return api<void>(`/api/v1/devices/delete/${id}`, { method: "DELETE" });
}

/** PUT /api/v1/devices/{id}/heartbeat */
export function updateDeviceHeartbeat(id: number) {
  return api<DeviceHeartbeatResponse>(`/api/v1/devices/${id}/heartbeat`, {
    method: "PUT",
  });
}

/** GET /api/v1/devices/search/{licenseKey}/{domain} */
export async function validateLicense(
  licenseKey: string,
  domain: string
): Promise<LicenseValidationResponse> {
  try {
    const rawResponse = await api<any>(
      `/api/v1/devices/search/${encodeURIComponent(licenseKey)}/${encodeURIComponent(
        domain
      )}`,
      {
        method: "GET",
      }
    );

    //   Check if response contains license data
    if (rawResponse && rawResponse.license_key) {
      //   Parse allowed_domains if it's a JSON string
      let allowedDomains: string[] = [];
      try {
        allowedDomains =
          typeof rawResponse.allowed_domains === "string"
            ? JSON.parse(rawResponse.allowed_domains)
            : rawResponse.allowed_domains || [];
      } catch (e) {
        console.warn("Failed to parse allowed_domains:", e);
        allowedDomains = [];
      }

      const isDomainAllowed = allowedDomains.includes(domain);
      const isActive = rawResponse.is_active === true;
      const isValid = isDomainAllowed && isActive;

      return {
        success: true,
        valid: isValid,
        data: {
          created_at: rawResponse.created_at,
          updated_at: rawResponse.updated_at,
          license_key: rawResponse.license_key,
          is_active: rawResponse.is_active,
          expires_at: rawResponse.expires_at,
          allowed_domains: rawResponse.allowed_domains,
          max_activations: rawResponse.max_activations,
          license_type: rawResponse.license_type,
        },
        message: isValid
          ? "License is valid and active for this domain"
          : !isActive
          ? "License is inactive"
          : !isDomainAllowed
          ? `License is not valid for domain: ${domain}`
          : "License validation failed",
      };
    } else {
      return {
        success: false,
        valid: false,
        message: "License not found or invalid response format",
      };
    }
  } catch (error: any) {
    console.error("Error validating license:", error);

    //   Handle specific error cases
    if (error.status === 404) {
      return {
        success: false,
        valid: false,
        message: "License key not found",
      };
    }

    return {
      success: false,
      valid: false,
      message: error.message || "Error validating license",
    };
  }
}
